<template>
  <div>
    <nav>
      <button
        v-for="(button, index) in buttonsExtractors"
        :key="index"
        @click="hasOneColor = button.option"
        :class="{ active: hasOneColor === button.option }"
      >
        {{ button.label }}
      </button>
    </nav>

    <div class="colors">
      <div class="btn-colors">
        <div
          v-for="(color, index) in colorCount()"
          :key="index"
          :style="{ background: newTheme[index] }"
        >
          <input type="color" v-model="newTheme[index]" />
        </div>
      </div>

      <button class="apply" @click="setColors">{{ parseTranslation(APPLY) }}</button>
    </div>

    <div class="themes">
      <div class="item-theme" v-for="(colorsTheme, index) in getThemes()" :key="index">
        <button class="btn-theme" @click="selectTheme(colorsTheme)">
          <div
            :style="{ background: colorsTheme[colorIndex] }"
            v-for="(color, colorIndex) in colorCount()"
            :key="colorIndex"
          ></div>
        </button>

        <button class="remove-theme" @click="removeTheme(index)">x</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import * as translationConfigItems from './configItem.translations'

import { CHART_TYPES } from '../chartConfig'

import { getFirstItem } from '@/utils/array'

import { RESULT_TYPES } from '../../questions.constants'

const { default: translations, ...translationKeys } = translationConfigItems

export default {
  name: 'ConfigColors',

  props: {
    question_result: {
      required: true,
      type: Object,
    },

    groupCode: {
      required: true,
    },

    defaultTheme: {
      required: true,
    },
  },

  data() {
    return {
      options: {
        one: true,
        multiple: false,
      },
      newTheme: [],
      themes: [],
      colors: [],
      hasOneColor: false,

      translations,
      ...translationKeys,
    }
  },

  computed: {
    buttonsExtractors() {
      const buttonsExtractors = [
        {
          label: this.parseTranslation(this.ONE_COLOR),
          option: this.options.one,
        },

        {
          label: this.parseTranslation(this.MULTIPLE_COLOR),
          option: this.options.multiple,
        },
      ]

      return buttonsExtractors
    },
  },

  watch: {
    hasOneColor() {
      this.newTheme = [...this.colors]

      this.setChartSettingsColors()
    },
  },

  methods: {
    ...mapActions(['setChartSettings']),

    colorCount() {
      const ONE_COLOR = 1

      const resultType = this.question_result.resultType

      const results = this.question_result?.results

      const isWordCloud = resultType === CHART_TYPES.WORD_CLOUD

      const resultsLength = results.length

      const subAnswersLength = getFirstItem(results)?.subAnswers?.length

      const isMatrix = resultType === RESULT_TYPES.MATRIX

      const chartColorCount = isMatrix ? subAnswersLength : resultsLength

      const WORD_CLOUD_COLOR_COUNT = 5

      const multiColors = isWordCloud ? WORD_CLOUD_COLOR_COUNT : chartColorCount

      return this.hasOneColor ? ONE_COLOR : multiColors
    },

    getThemes() {
      if (!this.hasOneColor) return this.themes

      const firstSingleColorThemes = [...new Set(this.themes.map(theme => theme[0]))]

      const themesOneColor = firstSingleColorThemes.map(color =>
        this.themes.find(theme => color === theme[0]),
      )

      return themesOneColor
    },

    setChartSettingsColors() {
      this.setChartSettings({
        setting: {
          ...this.question_result?.chartSettings,

          theme: {
            themes: this.themes,
            colors: this.colors,
            hasOneColor: this.hasOneColor,
          },
        },

        groupCode: this.groupCode,
        questionCode: this.question_result.code,
      })
    },

    setColors() {
      this.colors = [...this.newTheme]

      const hasTheme = this.themes.some(theme => this.isColors(theme))

      if (!hasTheme) {
        this.themes.push(this.colors)
      }

      this.setChartSettingsColors()
    },

    isColors(colors) {
      return (
        this.colors.length === colors.length &&
        !this.colors.some((color, index) => colors[index] !== color)
      )
    },

    selectTheme(colors) {
      this.newTheme = [...colors]
      this.setColors()
    },

    removeTheme(index) {
      this.themes.splice(index, 1)
    },
  },

  mounted() {
    this.colors = this.hasOneColor
      ? [this.defaultTheme[0]]
      : this.question_result?.chartSettings?.theme?.colors || this.defaultTheme

    this.newTheme = [...this.colors]

    this.themes = this.question_result?.chartSettings?.theme?.themes || []

    this.setColors()
  },
}
</script>

<style lang="scss" scoped>
.title {
  @include chart-config-title;
}

nav {
  @include buttons-nav;
}

.colors {
  @include flexAlignCenter;
  margin: 20px 0;

  .btn-colors {
    display: flex;
    padding: 5px 15px;
    border-radius: 3px;
    margin-right: 30px;

    div {
      @include boxSizing(25);
      margin-right: 10px;
      animation: show 0.3s linear;

      input {
        cursor: pointer;
        @include boxSizing(30);
        opacity: 0;
      }

      &:nth-last-of-type(1) {
        margin-right: 0;
      }
    }
  }

  .apply {
    @include primary-general-button;
    padding: 8px 10px;
    @include tipography(12);
    letter-spacing: 0.1em;
  }

  @media (max-width: 460px) {
    flex-wrap: wrap;

    .btn-colors {
      overflow-x: auto;
      overflow-y: hidden;

      &::-webkit-scrollbar {
        height: 8px;
        overflow: hidden;
      }

      &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.1);
        border-radius: 10px;
      }

      &::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 10px;
        height: 10px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: #aaa;
      }
    }

    .apply {
      margin-top: 16px;
    }
  }
}

.themes {
  margin-top: 20px;
  padding: 10px;
  display: flex;
  flex-wrap: wrap;
  overflow-y: auto;
  min-height: 56px;
  max-height: 95px;
  border-radius: 3px;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    overflow: hidden;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
    height: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #aaa;
  }

  .item-theme {
    position: relative;
    border: none;

    &:hover {
      .remove-theme {
        @include flexMiddle;
      }
    }

    .remove-theme {
      display: none;
      border: none;
      @include boxSizing(15);
      background: transparent;
      padding: 0;
      @include tipography(18);
      color: #df2938;
      position: absolute;
      top: 0px;
      right: 7px;
      box-shadow: 0px 8px 15px rgba(0, 0, 0, 0.1);
      animation: show 0.3s linear;
    }
  }
}

.btn-theme {
  position: relative;
  display: flex;
  border: 1px solid $gray;
  border-radius: 3px;
  background: transparent;
  padding: 5px;
  margin-right: 30px;
  margin-bottom: 10px;

  div {
    width: 20px;
    height: 20px;
  }
}
</style>
